// src/admin/components/Dashboard/FrontDeskDashboard.jsx
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  RiTeamLine, RiBuildingLine, RiMoneyDollarCircleLine, RiAlertLine,
  RiAddLine, RiRefreshLine, RiUserHeartLine, RiCalendarLine,
  RiHistoryLine, RiArrowRightLine, RiCheckboxCircleLine, RiTimeLine
} from 'react-icons/ri';
import { useTheme } from '../../../contexts/ThemeContext';
import { useAuth } from '../../../contexts/AuthContext';
import api from '../../../services/api';

const FrontDeskDashboard = () => {
  const { t } = useTranslation();
  const { isDarkMode } = useTheme();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [stats, setStats] = useState(null);
  const [recentPayments, setRecentPayments] = useState([]);
  const [alerts, setAlerts] = useState([]);

  const fetchData = async (showRefresh = false) => {
    try {
      showRefresh ? setRefreshing(true) : setLoading(true);
      const [statsRes, paymentsRes] = await Promise.all([
        api.get('/reports/dashboard-stats'),
        api.get('/reports/payment-logs', { params: { limit: 5 } })
      ]);
      const s = statsRes.data?.stats || {};
      setStats(s);
      setRecentPayments(paymentsRes.data?.paymentLogs || []);

      const newAlerts = [];
      if ((s.pending_payments_count || 0) > 0) {
        newAlerts.push({
          id: 'overdue', type: 'warning',
          title: t('overdue_payments'),
          message: t('overdue_payments_msg', { count: s.pending_payments_count, amount: `$${s.pending_payments_amount || 0}` }),
          link: '/admin/payments?status=pending'
        });
      }
      if (statsRes.data?.upcomingLeases?.length > 0) {
        newAlerts.push({
          id: 'leases', type: 'info',
          title: t('lease_expiring_soon'),
          message: t('lease_expiring_msg', { count: statsRes.data.upcomingLeases.length }),
          link: '/admin/tenants'
        });
      }
      setAlerts(newAlerts);
    } catch (err) {
      console.error('FrontDesk dashboard error:', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const card = 'rounded-xl border shadow-md ' + (isDarkMode ? 'bg-gray-850 border-gray-700' : 'bg-white border-gray-200');
  const textMuted = isDarkMode ? 'text-zinc-400' : 'text-gray-500';
  const textMain = isDarkMode ? 'text-white' : 'text-gray-900';

  const quickActions = [
    { label: t('add_tenant'), icon: RiUserHeartLine, path: '/admin/tenants/add', color: 'text-green-500' },
    { label: t('record_payment'), icon: RiMoneyDollarCircleLine, path: '/admin/payments/add', color: 'text-purple-500' },
    { label: t('view_details') + ' – ' + t('overdue_rent'), icon: RiHistoryLine, path: '/admin/tenants/overdue', color: 'text-red-500' },
    { label: t('tenant_list'), icon: RiTeamLine, path: '/admin/tenants', color: 'text-blue-500' },
  ];

  if (loading) {
    return (
      <div className="space-y-6">
        {[...Array(3)].map((_, i) => (
          <div key={i} className={`${card} p-6 animate-pulse`}>
            <div className="h-6 bg-gray-200 dark:bg-zinc-800 rounded w-1/3 mb-3" />
            <div className="h-8 bg-gray-200 dark:bg-zinc-800 rounded w-1/4" />
          </div>
        ))}
      </div>
    );
  }

  const statCards = [
    {
      label: t('active_tenants'),
      value: stats?.active_tenants ?? '—',
      icon: RiTeamLine,
      iconBg: 'bg-green-100 dark:bg-green-900/20 text-green-600 dark:text-green-400',
      path: '/admin/tenants'
    },
    {
      label: t('total_properties'),
      value: stats?.total_properties ?? '—',
      icon: RiBuildingLine,
      iconBg: 'bg-blue-100 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400',
      path: '/admin/apartments'
    },
    {
      label: t('pending_payments'),
      value: stats?.pending_payments_count ?? '0',
      icon: RiAlertLine,
      iconBg: 'bg-red-100 dark:bg-red-900/20 text-red-600 dark:text-red-400',
      path: '/admin/payments?status=pending'
    },
    {
      label: t('occupancy_rate'),
      value: `${stats?.occupancy_rate ?? 0}%`,
      icon: RiCheckboxCircleLine,
      iconBg: 'bg-purple-100 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400',
      path: '/admin/buildings'
    }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className={`${card} p-6`}>
        <div className="flex items-center justify-between">
          <div>
            <h1 className={`text-2xl font-bold ${textMain}`}>
              {t('welcome_back')}, {user?.firstName}! 👋
            </h1>
            <p className={`mt-1 ${textMuted}`}>{t('dashboard_subtitle')}</p>
          </div>
          <button
            onClick={() => fetchData(true)}
            disabled={refreshing}
            className={`p-2 rounded-lg ${isDarkMode ? 'bg-zinc-900 hover:bg-zinc-800 text-gray-300' : 'bg-gray-100 hover:bg-gray-200 text-gray-600'} transition-colors`}
          >
            <RiRefreshLine className={refreshing ? 'animate-spin' : ''} size={18} />
          </button>
        </div>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((s) => (
          <div
            key={s.label}
            onClick={() => navigate(s.path)}
            className={`${card} p-5 cursor-pointer hover:shadow-lg transition-shadow`}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className={`text-sm font-medium ${textMuted}`}>{s.label}</p>
                <p className={`text-3xl font-bold mt-1 ${textMain}`}>{s.value}</p>
              </div>
              <div className={`p-3 rounded-full ${s.iconBg}`}>
                <s.icon size={24} />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Alerts */}
      {alerts.length > 0 && (
        <div className={`${card}`}>
          <div className="p-5 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
            <h3 className={`font-semibold ${textMain} flex items-center gap-2`}>
              <RiAlertLine className="text-orange-500" /> {t('alerts_notifications')}
            </h3>
            <span className="text-xs bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-400 px-2 py-1 rounded-full">{alerts.length}</span>
          </div>
          <div className="p-5 space-y-3">
            {alerts.map(a => (
              <div
                key={a.id}
                onClick={() => navigate(a.link)}
                className={`flex items-start justify-between p-4 rounded-lg border cursor-pointer transition-colors ${
                  a.type === 'warning'
                    ? 'bg-yellow-50 border-yellow-200 text-yellow-800 dark:bg-yellow-900/20 dark:border-yellow-800 dark:text-yellow-400'
                    : 'bg-blue-50 border-blue-200 text-blue-800 dark:bg-blue-900/20 dark:border-blue-800 dark:text-blue-400'
                }`}
              >
                <div>
                  <p className="font-medium text-sm">{a.title}</p>
                  <p className="text-xs mt-1">{a.message}</p>
                </div>
                <RiArrowRightLine size={18} className="mt-0.5 flex-shrink-0" />
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent Payments */}
        <div className={`lg:col-span-2 ${card}`}>
          <div className="p-5 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
            <h3 className={`font-semibold ${textMain}`}>{t('recent_transactions')}</h3>
            <button onClick={() => navigate('/admin/payments')} className="text-sm text-red-500 hover:text-red-400">
              {t('view_all')}
            </button>
          </div>
          <div className="p-5 space-y-3">
            {recentPayments.length === 0 ? (
              <p className={`text-sm ${textMuted} text-center py-4`}>{t('no_recent_payments')}</p>
            ) : recentPayments.map((p) => (
              <div key={p.paymentId} className={`flex items-center justify-between p-3 rounded-lg ${isDarkMode ? 'hover:bg-zinc-900' : 'hover:bg-gray-50'} transition-colors`}>
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-full bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400">
                    <RiMoneyDollarCircleLine size={16} />
                  </div>
                  <div>
                    <p className={`text-sm font-medium ${textMain}`}>
                      {p.paymentType} — Unit {p.apartmentNumber}
                    </p>
                    <p className={`text-xs ${textMuted}`}>{new Date(p.paymentDate).toLocaleDateString()}</p>
                  </div>
                </div>
                <p className={`text-sm font-semibold ${textMain}`}>
                  ${Number(p.amount || 0).toLocaleString()}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Quick Actions */}
        <div className={card}>
          <div className="p-5 border-b border-gray-200 dark:border-gray-700">
            <h3 className={`font-semibold ${textMain}`}>{t('quick_actions')}</h3>
          </div>
          <div className="p-5 space-y-3">
            {quickActions.map((a) => (
              <button
                key={a.label}
                onClick={() => navigate(a.path)}
                className={`w-full flex items-center gap-3 p-4 rounded-lg border-2 border-dashed text-left transition-all ${
                  isDarkMode
                    ? 'border-gray-700 hover:border-red-500 hover:bg-red-900/10'
                    : 'border-gray-200 hover:border-red-400 hover:bg-red-50'
                }`}
              >
                <a.icon className={a.color} size={20} />
                <span className={`text-sm font-medium ${textMain}`}>{a.label}</span>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FrontDeskDashboard;
